import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Plus } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loading, Empty, KPI, AttStatusBadge } from "./_shared";

export default function AttendanceLogs({ staff }) {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [source, setSource] = useState("all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (date) params.append("date", date);
      if (source !== "all") params.append("source", source);
      const { data } = await api.get(`/hr/attendance/logs?${params}`);
      setItems(data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date, source]);

  const present = items.filter((i) => i.status === "present").length;
  const late = items.filter((i) => i.status === "late").length;
  const absent = items.filter((i) => i.status === "absent").length;
  const fpCount = items.filter((i) => i.source === "fingerprint").length;

  return (
    <div className="space-y-4" data-testid="attendance-logs-tab">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KPI label="Present" value={present} tone="emerald" />
        <KPI label="Late" value={late} tone="amber" />
        <KPI label="Absent" value={absent} tone="red" />
        <KPI label="Fingerprint Scans" value={fpCount} tone="indigo" />
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Label className="text-xs">Date:</Label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-40" data-testid="attendance-date-filter" />
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs">Source:</Label>
          <Select value={source} onValueChange={setSource}>
            <SelectTrigger className="w-40" data-testid="attendance-source-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sources</SelectItem>
              <SelectItem value="fingerprint">Fingerprint</SelectItem>
              <SelectItem value="manual">Manual</SelectItem>
              <SelectItem value="rfid">RFID</SelectItem>
              <SelectItem value="mobile">Mobile</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="ml-auto">
          <FingerprintEventDialog staff={staff} open={open} onOpenChange={setOpen} onLogged={load} />
        </div>
      </div>

      <div className="dc-card overflow-hidden">
        {loading ? <Loading /> : items.length === 0 ? <Empty text="No attendance records for this filter." /> : (
          <table className="w-full">
            <thead className="bg-slate-50 text-left text-[11px] font-mono-alt uppercase tracking-widest text-slate-500">
              <tr>
                <th className="px-5 py-3">Employee</th>
                <th className="px-5 py-3">Date</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3">Source</th>
                <th className="px-5 py-3">Check-in</th>
                <th className="px-5 py-3">Check-out</th>
                <th className="px-5 py-3">Device</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((i) => (
                <tr key={i.id || `${i.staff_id}-${i.date}`} className="text-sm">
                  <td className="px-5 py-3">
                    <div className="font-semibold text-slate-900">{i.staff_name}</div>
                    <div className="text-[10px] text-slate-500 font-mono-alt">{i.employee_code} · {i.department}</div>
                  </td>
                  <td className="px-5 py-3 font-mono-alt text-xs">{i.date}</td>
                  <td className="px-5 py-3"><AttStatusBadge status={i.status} /></td>
                  <td className="px-5 py-3">
                    <span className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-mono-alt uppercase tracking-wider bg-indigo-50 text-indigo-700">
                      {i.source || "manual"}
                    </span>
                    {i.match_score != null && <span className="ml-1 text-[10px] text-slate-500">· {i.match_score}%</span>}
                  </td>
                  <td className="px-5 py-3 font-mono-alt text-xs">{i.check_in ? new Date(i.check_in).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                  <td className="px-5 py-3 font-mono-alt text-xs">{i.check_out ? new Date(i.check_out).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                  <td className="px-5 py-3 text-xs text-slate-500">{i.device_id || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function FingerprintEventDialog({ staff, open, onOpenChange, onLogged }) {
  const [form, setForm] = useState({ staff_id: "", device_id: "DEV-FP-01", event_type: "check_in", match_score: 98, notes: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/hr/attendance/fingerprint", { ...form, match_score: Number(form.match_score) });
      toast.success(`Fingerprint ${form.event_type.replace("_", " ")} logged`);
      onOpenChange(false); onLogged?.();
      setForm({ staff_id: "", device_id: "DEV-FP-01", event_type: "check_in", match_score: 98, notes: "" });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button className="btn-primary-blue" data-testid="log-fingerprint-btn"><Plus size={14} /> Log Fingerprint Scan</Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Log Fingerprint Attendance</DialogTitle>
          <DialogDescription>Simulate or record a biometric device scan.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Employee *</Label>
            <Select value={form.staff_id} onValueChange={(v) => setForm({ ...form, staff_id: v })}>
              <SelectTrigger data-testid="fingerprint-staff-select"><SelectValue placeholder="Select employee…" /></SelectTrigger>
              <SelectContent>{staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.full_name} ({s.employee_code || "—"})</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Event *</Label>
              <Select value={form.event_type} onValueChange={(v) => setForm({ ...form, event_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="check_in">Check-in</SelectItem>
                  <SelectItem value="check_out">Check-out</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Device ID *</Label><Input required value={form.device_id} onChange={(e) => setForm({ ...form, device_id: e.target.value })} /></div>
            <div><Label>Match score (%)</Label><Input type="number" min={0} max={100} value={form.match_score} onChange={(e) => setForm({ ...form, match_score: e.target.value })} /></div>
            <div><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={busy || !form.staff_id} className="btn-primary-blue" data-testid="fingerprint-submit-btn">
              {busy ? "Logging…" : "Log Scan"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
